import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { chartOfAccountService } from '../../services/chartOfAccount.service';
import {
  ChartOfAccount,
  CreateChartOfAccountDto,
  UpdateChartOfAccountDto,
  AccountType,
  AccountNature,
  ACCOUNT_TYPES,
  ACCOUNT_NATURES,
  getAccountNatureByType,
} from '../../types/chartOfAccount';
import config from '../../config';
import axios from 'axios';

const ChartOfAccountForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const isEditMode = Boolean(id);

  // Translations
  const t = {
    title: isEditMode 
      ? (isArabic ? 'تعديل حساب' : 'Edit Account')
      : (isArabic ? 'حساب جديد' : 'New Account'),
    accountCode: isArabic ? 'رقم الحساب' : 'Account Code',
    accountCodeHelper: isArabic 
      ? 'توليد تلقائي حسب إعدادات المستويات' 
      : 'Auto-generated based on level settings',
    accountCodeManual: isArabic ? 'أدخل رقم الحساب (مثال: 01، 02، 03)' : 'Enter code (e.g., 01, 02, 03)',
    parentAccount: isArabic ? 'الحساب الأب' : 'Parent Account',
    optional: isArabic ? 'اختياري' : 'Optional',
    currency: isArabic ? 'العملة' : 'Currency',
    accountNameAr: isArabic ? 'اسم الحساب (عربي)' : 'Account Name (Arabic)',
    accountNameEn: isArabic ? 'اسم الحساب (إنجليزي)' : 'Account Name (English)',
    accountType: isArabic ? 'نوع الحساب' : 'Account Type',
    accountNature: isArabic ? 'طبيعة الحساب' : 'Account Nature',
    openingBalance: isArabic ? 'الرصيد الافتتاحي' : 'Opening Balance',
    active: isArabic ? 'نشط' : 'Active',
    mainAccount: isArabic ? 'حساب رئيسي' : 'Main Account',
    finalAccount: isArabic ? 'حساب نهائي' : 'Final Account',
    manualEntry: isArabic ? 'إدخال يدوي' : 'Manual Entry',
    requireCostCenter: isArabic ? 'مركز تكلفة إجباري' : 'Require Cost Center',
    descriptionAr: isArabic ? 'الوصف (عربي)' : 'Description (Arabic)',
    descriptionEn: isArabic ? 'الوصف (إنجليزي)' : 'Description (English)',
    notes: isArabic ? 'ملاحظات' : 'Notes',
    cancel: isArabic ? 'إلغاء' : 'Cancel',
    save: isArabic ? 'حفظ' : 'Save',
    saving: isArabic ? 'جاري الحفظ...' : 'Saving...',
  };

  const [availableParentAccounts, setAvailableParentAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Account Code Zeros settings from SystemSettings
  const [levelZeros, setLevelZeros] = useState<number[]>([1, 2, 3, 4, 5, 6]);

  const [formData, setFormData] = useState<Partial<CreateChartOfAccountDto>>({
    accountCode: '',
    accountName: '',
    accountNameAr: '',
    parentAccountId: undefined,
    accountType: 'Asset',
    accountNature: 'Debit',
    isActive: true,
    isMainAccount: false,
    isFinalAccount: true,
    currencyCode: 'KWD',
    allowManualEntry: true,
    requireCostCenter: false,
    openingBalance: 0,
    description: '',
    descriptionAr: '',
    notes: '',
  });

  useEffect(() => {
    loadAccounts();
    loadLevelZerosSettings();
    if (isEditMode && id) {
      loadAccount(parseInt(id));
    }
  }, [id, isEditMode]);

  // Auto-set account nature when type changes
  useEffect(() => {
    if (formData.accountType) {
      const nature = getAccountNatureByType(formData.accountType);
      setFormData((prev) => ({ ...prev, accountNature: nature }));
    }
  }, [formData.accountType]);

  const loadLevelZerosSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/SystemSettings/category/ChartOfAccounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const settings = response.data;
      const loaded = [...levelZeros];
      for (let i = 1; i <= 6; i++) {
        const val = settings[`AccountCodeZeros_Level${i}`];
        if (val !== undefined && val !== null) {
          loaded[i - 1] = parseInt(val);
        }
      }
      setLevelZeros(loaded);
    } catch (err) {
      console.warn('Could not load AccountCodeZeros settings, using defaults');
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await chartOfAccountService.getAll({ includeInactive: false });
      // Filter out final accounts - they cannot be parents
      // Also filter out current account in edit mode (account cannot be its own parent)
      const nonFinalAccounts = data.filter(acc => {
        if (acc.isFinalAccount) return false;
        if (isEditMode && id && acc.accountId === parseInt(id)) return false;
        return true;
      });
      setAvailableParentAccounts(nonFinalAccounts);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadAccount = async (accountId: number) => {
    try {
      setLoading(true);
      const data = await chartOfAccountService.getById(accountId);
      setFormData({
        accountCode: data.accountCode,
        accountName: data.accountName,
        accountNameAr: data.accountNameAr,
        parentAccountId: data.parentAccountId,
        accountType: data.accountType as AccountType,
        accountNature: data.accountNature as AccountNature,
        isActive: data.isActive,
        isMainAccount: data.isMainAccount,
        isFinalAccount: data.isFinalAccount,
        currencyCode: data.currencyCode,
        allowManualEntry: data.allowManualEntry,
        requireCostCenter: data.requireCostCenter,
        openingBalance: data.openingBalance,
        description: data.description || '',
        descriptionAr: data.descriptionAr || '',
        notes: data.notes || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || (isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  // Generate next account code based on parent
  const generateAccountCode = async (parentId: number) => {
    try {
      const parent = availableParentAccounts.find(acc => acc.accountId === parentId);
      if (!parent) return;

      // Calculate level based on number of dashes in parent code
      // Parent level = number of dashes + 1
      // Current level = parent level + 1
      const dashCount = (parent.accountCode.match(/-/g) || []).length;
      const currentLevel = dashCount + 2;

      // Get padding from settings (zeros + 1 = total digits)
      // levelZeros array: index 0 = Level 1, index 1 = Level 2, etc.
      // currentLevel is 1-based, so index = currentLevel - 1
      const levelIndex = Math.min(currentLevel - 1, levelZeros.length - 1);
      const zeros = levelZeros[levelIndex];
      const padding = zeros + 1; // e.g. 2 zeros = 3 total digits (001)

      // Get all children of this parent
      const allAccounts = await chartOfAccountService.getAll({ includeInactive: true });
      const siblings = allAccounts.filter(acc => acc.parentAccountId === parentId);

      let nextNumber = 1;
      if (siblings.length > 0) {
        // Extract numbers from sibling codes and find max
        const numbers = siblings.map(sibling => {
          const parts = sibling.accountCode.split('-');
          const lastPart = parts[parts.length - 1];
          return parseInt(lastPart) || 0;
        });
        nextNumber = Math.max(...numbers) + 1;
      }

      // Generate new code with proper padding
      const newCode = `${parent.accountCode}-${nextNumber.toString().padStart(padding, '0')}`;
      setFormData(prev => ({ ...prev, accountCode: newCode }));
    } catch (err) {
      console.error('Error generating account code:', err);
    }
  };

  const handleChange = (field: keyof CreateChartOfAccountDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate code when parent is selected
    if (field === 'parentAccountId' && value && !isEditMode) {
      generateAccountCode(value as number);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        const updateData: UpdateChartOfAccountDto = {
          accountId: parseInt(id),
          accountCode: formData.accountCode!,
          accountName: formData.accountName!,
          accountNameAr: formData.accountNameAr!,
          parentAccountId: formData.parentAccountId,
          accountType: formData.accountType!,
          accountNature: formData.accountNature!,
          isActive: formData.isActive ?? true,
          isMainAccount: formData.isMainAccount ?? false,
          isFinalAccount: formData.isFinalAccount ?? true,
          currencyCode: formData.currencyCode ?? 'KWD',
          allowManualEntry: formData.allowManualEntry ?? true,
          requireCostCenter: formData.requireCostCenter ?? false,
          openingBalance: formData.openingBalance ?? 0,
          description: formData.description,
          descriptionAr: formData.descriptionAr,
          notes: formData.notes,
        };
        await chartOfAccountService.update(parseInt(id), updateData);
        toast.success(isArabic ? 'تم التحديث بنجاح' : 'Updated successfully');
      } else {
        const createData: CreateChartOfAccountDto = formData as CreateChartOfAccountDto;
        await chartOfAccountService.create(createData);
        toast.success(isArabic ? 'تم الحفظ بنجاح' : 'Saved successfully');
      }
      navigate('/chart-of-accounts');
    } catch (err: any) {
      setError(err.response?.data?.message || (isArabic ? 'حدث خطأ أثناء الحفظ' : 'Error saving'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" component="h1" mb={3} fontWeight="bold" color="primary.main">
        {t.title}
      </Typography>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Row 1: Account Code, Parent Account, Currency */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Account Code */}
            <Box flex={1}>
              <TextField
                fullWidth
                required
                label={t.accountCode}
                value={formData.accountCode}
                onChange={(e) => handleChange('accountCode', e.target.value)}
                helperText={
                  formData.parentAccountId 
                    ? t.accountCodeHelper 
                    : t.accountCodeManual
                }
                disabled={false}
              />
            </Box>

            {/* Parent Account */}
            <Box flex={1}>
              <Autocomplete
                options={availableParentAccounts}
                getOptionLabel={(option) => `${option.accountCode} - ${isArabic ? option.accountNameAr : option.accountName}`}
                value={
                  availableParentAccounts.find((acc) => acc.accountId === formData.parentAccountId) ||
                  null
                }
                onChange={(_, newValue) => handleChange('parentAccountId', newValue?.accountId)}
                renderInput={(params) => (
                  <TextField {...params} label={t.parentAccount} placeholder={t.optional} />
                )}
              />
            </Box>

            {/* Currency Code */}
            <Box flex={1}>
              <TextField
                fullWidth
                label={t.currency}
                value={formData.currencyCode}
                onChange={(e) => handleChange('currencyCode', e.target.value)}
                inputProps={{ maxLength: 3 }}
              />
            </Box>
          </Stack>

          {/* Row 2: Account Names */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Account Name (Arabic) */}
            <Box flex={1}>
              <TextField
                fullWidth
                required
                label={t.accountNameAr}
                value={formData.accountNameAr}
                onChange={(e) => handleChange('accountNameAr', e.target.value)}
              />
            </Box>

            {/* Account Name (English) */}
            <Box flex={1}>
              <TextField
                fullWidth
                required
                label={t.accountNameEn}
                value={formData.accountName}
                onChange={(e) => handleChange('accountName', e.target.value)}
              />
            </Box>
          </Stack>

          {/* Row 3: Account Type, Nature */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Account Type */}
            <Box flex={1}>
              <FormControl fullWidth required>
                <InputLabel>{t.accountType}</InputLabel>
                <Select
                  value={formData.accountType}
                  label={t.accountType}
                  onChange={(e) => handleChange('accountType', e.target.value as AccountType)}
                >
                  {ACCOUNT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {isArabic ? type.labelAr : type.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Account Nature (Auto-set based on type) */}
            <Box flex={1}>
              <FormControl fullWidth required disabled>
                <InputLabel>{t.accountNature}</InputLabel>
                <Select
                  value={formData.accountNature}
                  label={t.accountNature}
                  onChange={(e) => handleChange('accountNature', e.target.value as AccountNature)}
                >
                  {ACCOUNT_NATURES.map((nature) => (
                    <MenuItem key={nature.value} value={nature.value}>
                      {isArabic ? nature.labelAr : nature.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {/* Row 4: Opening Balance, Switches */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Opening Balance */}
            <Box flex={1}>
              <TextField
                fullWidth
                type="number"
                label={t.openingBalance}
                value={formData.openingBalance}
                onChange={(e) => handleChange('openingBalance', parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.001, min: 0 }}
              />
            </Box>

            <Box flex={1}>
              <Box display="flex" gap={2} height="100%" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                  }
                  label={t.active}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isMainAccount}
                      onChange={(e) => handleChange('isMainAccount', e.target.checked)}
                    />
                  }
                  label={t.mainAccount}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFinalAccount}
                      onChange={(e) => handleChange('isFinalAccount', e.target.checked)}
                    />
                  }
                  label={t.finalAccount}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowManualEntry}
                      onChange={(e) => handleChange('allowManualEntry', e.target.checked)}
                    />
                  }
                  label={t.manualEntry}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requireCostCenter}
                      onChange={(e) => handleChange('requireCostCenter', e.target.checked)}
                    />
                  }
                  label={t.requireCostCenter}
                />
              </Box>
            </Box>
          </Stack>

          <Divider />

          {/* Row 5: Descriptions */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Description (Arabic) */}
            <Box flex={1}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t.descriptionAr}
                value={formData.descriptionAr}
                onChange={(e) => handleChange('descriptionAr', e.target.value)}
              />
            </Box>

            {/* Description (English) */}
            <Box flex={1}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t.descriptionEn}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Box>
          </Stack>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label={t.notes}
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
          />

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/chart-of-accounts')}
              disabled={saving}
            >
              {t.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? t.saving : t.save}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ChartOfAccountForm;